import { Autocomplete, InputAdornment, SxProps, TextField, TextFieldProps, Theme, Tooltip } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { MutableRefObject } from "react";
import { useDefaultValues } from "./defaultValues";
import { dateFormatByLandCode, dateInputRegexByLandCode, dateTimeFormatByLandCode, formatNumber, monthFormatByLandCode, monthInputRegexByLandCode, parseNumber } from "./localization";
import { ifNull, isoDateFormat, isoMonthFormat } from "./utils";
import { Property } from "csstype";
import { dayjs, timeZoneMap } from "./config";
import { BaseDatePickerProps } from "@mui/x-date-pickers/DatePicker/shared";

export const BgColorNegative = "#ffc7b8";
export const BgColorSemiNegative = "#fff4d5";

export const InputSizedButtonSx: SxProps<Theme> = { height: 37.44, minWidth: 37, marginTop: "7px !important", marginBottom: "8px !important" };
export const InputSizedSaveButtonSx: SxProps<Theme> = { ...InputSizedButtonSx, width: 120 };

export function NumberField(props: {
    value: number | null | undefined;
    valueFormatted?: string | null;
    setValue?(value: number | null, valueFormatted: string): void;
    label: string;
    error?: string | false;
    disabled?: boolean;
    readOnly?: boolean;
    isCurrency?: boolean;
    currency?: string;
    unit?: string;
    textAlign?: Property.TextAlign;
    transformValue?(value: number | null | undefined): number | null | undefined;
    inputRef?: MutableRefObject<HTMLInputElement | undefined>;
    color?: TextFieldProps["color"];
    focused?: TextFieldProps["focused"];
    style?: TextFieldProps["style"];
    showErrorText?: boolean;
    onBlur?(): void;
    sx?: SxProps<Theme>;
    confirmChange?(newValue: number | null): Promise<boolean>;
    showTooltipError?: boolean;
    onKeyDown?: React.KeyboardEventHandler<HTMLDivElement> | undefined
    autoFocus?: boolean;
    autocompleteValues?: number[];
}) {
    const { landCode, currency } = useDefaultValues(p => ({ landCode: p.clientData.landCode, currency: p.clientData.currency }));
    const inputValue = props.valueFormatted != null ? props.valueFormatted : props.value == null ? "" :
        formatNumber(props.transformValue == null ? props.value : props.transformValue(props.value), landCode, props.isCurrency || false);
    const endAdornment = !props.isCurrency && props.unit == null ? undefined :
        <InputAdornment position="end" style={{ paddingTop: 1 }}>{ifNull(props.unit, ifNull(props.currency, currency))}</InputAdornment>;
    const showTooltipError = props.showTooltipError === true;
    const showErrorText = props.showErrorText === true || !showTooltipError;
    return <Tooltip title={props.error} arrow disableHoverListener open={showTooltipError ? undefined : false}>
        {props.autocompleteValues == null ? <TextField value={inputValue} label={props.label} InputProps={{
            readOnly: props.readOnly, endAdornment, disabled: props.disabled,
        }}
            color={props.color} focused={props.focused} style={props.style} sx={props.sx} onBlur={() => props.onBlur?.()}
            onKeyDown={p => {
                if (p.key === "Enter") {
                    props.onBlur?.();
                }
                props.onKeyDown?.(p);
            }}
            autoFocus={props.autoFocus}
            inputProps={{ style: { textAlign: props.textAlign || "right" } }}
            error={!!props.error} helperText={showErrorText ? props.error : undefined}
            onChange={p => {
                const textValue = p.target.value;
                const value = parseNumber(textValue, landCode);
                (async () => {
                    if (props.confirmChange != null && !await props.confirmChange(value)) {
                        return;
                    }
                    if (props.setValue) {
                        props.setValue(value, textValue);
                    }
                })();
            }} inputRef={props.inputRef && (p => props.inputRef!.current = p)} /> :
            <Autocomplete freeSolo disableClearable={true} options={props.autocompleteValues}
                getOptionLabel={p => typeof p === "number" ? formatNumber(p, landCode, props.isCurrency || false) : p}
                renderOption={(attrs, option, state) => {
                    const label = formatNumber(option, landCode, props.isCurrency || false);
                    const selected = state.inputValue === label;
                    return <li key={option} style={{ flexDirection: (props.textAlign || "right") === "right" ? "row-reverse" : "row" }}
                        {...{ ...attrs, "aria-selected": selected }}>
                        {label}
                    </li>;
                }}
                value={inputValue} onInputChange={(e, textValue) => {
                    const value = parseNumber(textValue, landCode);
                    (async () => {
                        if (props.confirmChange != null && !await props.confirmChange(value)) {
                            return;
                        }
                        if (props.setValue) {
                            props.setValue(value, textValue);
                        }
                    })();
                }} renderInput={(params) => <TextField {...params} label={props.label}
                    inputProps={{ ...params.inputProps, style: { textAlign: props.textAlign || "right" } }}
                    error={!!props.error} helperText={showErrorText ? props.error : undefined}
                    InputProps={{ ...params.InputProps, endAdornment: endAdornment ?? params.InputProps.endAdornment }}
                    autoFocus={props.autoFocus}
                />} />}
    </Tooltip>;
}

export function DateField(props: {
    value: string | null;
    valueFormatted?: string | null;
    setValue?(value: string | null, valueFormatted: string): void;
    label: string;
    error?: string | false;
    disabled?: boolean;
    readOnly?: boolean;
    onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
    type?: "datetime" | "month";
    inputRef?: React.Ref<HTMLInputElement>;
}) {
    const { landCode } = useDefaultValues(p => ({ landCode: p.clientData.landCode }));
    const format = props.type === "datetime" ? dateTimeFormatByLandCode[landCode] :
        props.type === "month" ? monthFormatByLandCode[landCode] :
            dateFormatByLandCode[landCode];
    const regex = props.type === "datetime" ? null! :
        props.type === "month" ? monthInputRegexByLandCode[landCode] :
            dateInputRegexByLandCode[landCode];
    const dataFormat = props.type === "month" ? isoMonthFormat : isoDateFormat;
    const inputValue = props.valueFormatted != null ? props.valueFormatted : props.value ? dayjs.tz(props.value as string).format(format) : "";
    const pickerProps: Partial<BaseDatePickerProps<dayjs.Dayjs | null, dayjs.Dayjs | null>> = {};
    if (props.type === "month") {
        pickerProps.openTo = "month";
    }
    return <DesktopDatePicker
        {...pickerProps}
        views={props.type === "month" ? ["year", "month"] : undefined}
        value={(props.value ?? "").trim() !== "" ? dayjs.tz(props.value as string) : null}
        onChange={value => {
            if (props.setValue != null) {
                const finalValue = value != null && !value.isValid() ? null : value;
                props.setValue(finalValue == null ? null : finalValue.format(dataFormat), finalValue == null ? "" : finalValue.format(format));
            }
        }}
        inputRef={props.inputRef}
        readOnly={props.readOnly}
        disabled={props.disabled}
        renderInput={inputProps => {
            return <TextField value={inputValue} label={props.label} disabled={props.disabled} InputProps={{ ...inputProps.InputProps, readOnly: props.readOnly }}
                inputRef={inputProps.inputRef} error={props.error != null} helperText={props.error} onChange={p => {
                    if (props.setValue != null) {
                        const value = p.target.value.trim() === "" || !regex.test(p.target.value.replaceAll(" ", "")) ? null : dayjs.tz(p.target.value, format.replaceAll(" ", ""), timeZoneMap[landCode]);
                        const finalValue = value != null && !value.isValid() ? null : value;
                        props.setValue(finalValue != null ? finalValue.format(dataFormat) : null, p.target.value);
                    }
                }} onKeyDown={props.onKeyDown} />
        }}
    />;
}
